import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Clock, Landmark, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TERMS = [7, 15, 30];

export default function CreditApplyPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "", contact_name: user?.name || "", phone: "",
    business_address: "", gstin: "", requested_limit: "", requested_term_days: 30,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [pendingExisting, setPendingExisting] = useState(null);

  useEffect(() => {
    if (!user) return;
    api.get("/credit/account").then(({ data }) => {
      if (data.credit_status !== "NOT_ELIGIBLE") {
        navigate("/account/credit", { replace: true });
      } else if (data.application && ["PENDING_REVIEW", "MORE_INFO"].includes(data.application.status)) {
        setPendingExisting(data.application);
      }
    }).catch(() => {});
  }, [user, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "h-12 w-full rounded-xl border border-line bg-input px-4 text-sm text-1 placeholder:text-[var(--pq-text3)] focus:border-amber-500/60 focus:outline-none";
  const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.15em] text-3";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/credit/apply", { ...form, requested_limit: parseFloat(form.requested_limit) || 0, requested_term_days: parseInt(form.requested_term_days) });
      setDone(true);
    } catch (err) {
      toast.error(apiError(err, "Couldn't submit your application"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6">
        <button onClick={() => navigate("/login", { state: { from: "/account/credit/apply" } })} className="rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-black" data-testid="apply-login-cta">
          Sign in to apply
        </button>
      </div>
    );
  }

  if (done || pendingExisting) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pb-24 pt-28" data-testid="apply-success">
        <div className="max-w-md text-center">
          {pendingExisting ? (
            <>
              <Clock className="mx-auto h-12 w-12 text-amber-400" />
              <h1 className="font-display mt-5 text-3xl font-semibold tracking-tight">Application Under Review</h1>
              <span className="mt-4 inline-block rounded-full border border-amber-500/40 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-400">
                {pendingExisting.status === "MORE_INFO" ? "Info Requested" : "Pending Review"}
              </span>
              <p className="mt-4 text-sm leading-relaxed text-2">
                Your application has been submitted. PAQVERO will review your request and notify you once it's decided.
              </p>
            </>
          ) : (
            <>
              <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
              <h1 className="font-display mt-5 text-3xl font-semibold tracking-tight">Application Submitted</h1>
              <span className="mt-4 inline-block rounded-full border border-amber-500/40 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-400">Pending Review</span>
              <p className="mt-4 text-sm leading-relaxed text-2">
                Your application has been submitted. PAQVERO will review your request and notify you once it's decided.
              </p>
            </>
          )}
          <button onClick={() => navigate("/shop")} className="mt-7 rounded-full bg-amber-500 px-8 py-3.5 text-sm font-bold text-black hover:bg-amber-400" data-testid="apply-continue">
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="credit-apply-page">
      <div className="mx-auto max-w-2xl px-6">
        <div className="text-center">
          <Landmark className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="font-display mt-5 text-4xl font-semibold tracking-tighter">Apply for PAQVERO Credit</h1>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-2">
            Eligible business customers may request payment terms of up to 30 days. Applications are reviewed manually — nothing is approved automatically.
          </p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-5 rounded-2xl border border-line bg-surface p-7">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Business name</label>
              <input data-testid="apply-business" required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} className={inputCls} placeholder="Mehta Traders Pvt. Ltd." />
            </div>
            <div>
              <label className={labelCls}>Contact person</label>
              <input data-testid="apply-contact" required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} className={inputCls} placeholder="Aarav Mehta" />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input data-testid="apply-email" value={user.email} disabled className={`${inputCls} opacity-50`} />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="apply-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="98200 12345" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Business address</label>
            <textarea data-testid="apply-address" required rows={3} value={form.business_address} onChange={(e) => set("business_address", e.target.value)} className={`${inputCls} h-auto py-3`} placeholder="Registered business address" />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label className={labelCls}>GSTIN (if applicable)</label>
              <input data-testid="apply-gstin" value={form.gstin} onChange={(e) => set("gstin", e.target.value)} className={inputCls} placeholder="27AABCM1234A1Z5" />
            </div>
            <div>
              <label className={labelCls}>Requested credit limit ₹</label>
              <input data-testid="apply-limit" required type="number" min="1000" value={form.requested_limit} onChange={(e) => set("requested_limit", e.target.value)} className={inputCls} placeholder="50000" />
            </div>
            <div>
              <label className={labelCls}>Requested term</label>
              <select data-testid="apply-term" value={form.requested_term_days} onChange={(e) => set("requested_term_days", e.target.value)} className={inputCls}>
                {TERMS.map((t) => <option key={t} value={t}>{`${t} days`}</option>)}
              </select>
            </div>
          </div>
          <button data-testid="apply-submit" type="submit" disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-4 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit Application
          </button>
          <p className="text-center text-[11px] leading-relaxed text-3">
            Credit terms are subject to PAQVERO's review and the applicable trade-credit agreement.
          </p>
        </form>
      </div>
    </div>
  );
}
